import { useState, useRef, useCallback, useEffect } from 'react';
import {
  useTextToSpeechMutation,
  useSpeechToTextMutation,
} from '../store/api';

export type VoiceState = 'idle' | 'recording' | 'transcribing' | 'speaking';

interface UseVoiceChatOptions {
  /** Automatically speak agent responses when voice mode is on */
  autoSpeak?: boolean;
  /** TTS voice name */
  voice?: string;
  /** TTS model */
  model?: string;
  /** Called with transcribed text when STT completes */
  onTranscript?: (text: string) => void;
}

export function useVoiceChat(options: UseVoiceChatOptions = {}) {
  const {
    autoSpeak = true,
    voice = 'nova',
    model = 'tts-1',
    onTranscript,
  } = options;

  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [tts] = useTextToSpeechMutation();
  const [stt] = useSpeechToTextMutation();

  useEffect(() => {
    audioElementRef.current = new Audio();
    audioElementRef.current.addEventListener('ended', () => {
      setVoiceState((prev) => (prev === 'speaking' ? 'idle' : prev));
    });
    return () => {
      audioElementRef.current?.pause();
      audioElementRef.current = null;
      stopRecording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
    mediaRecorderRef.current = null;
  }, []);

  const toggleVoice = useCallback(() => {
    setVoiceEnabled((prev) => !prev);
    if (voiceState === 'recording') {
      stopRecording();
    }
    if (voiceState === 'speaking') {
      audioElementRef.current?.pause();
      setVoiceState('idle');
    }
  }, [voiceState, stopRecording]);

  const startRecording = useCallback(async () => {
    setError(null);
    setVoiceEnabled(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (blob.size < 100) {
          setVoiceState('idle');
          return;
        }

        setVoiceState('transcribing');

        try {
          const base64 = await blobToBase64(blob);
          const result = await stt({
            audio: base64,
            'content-type': 'audio/webm',
          }).unwrap();

          if (result.text) {
            onTranscript?.(result.text);
          }
        } catch (err) {
          setError('Transcription failed. Please try again.');
          console.error('STT error:', err);
        } finally {
          setVoiceState('idle');
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setVoiceState('recording');
    } catch (err) {
      setError('Microphone access denied. Please allow microphone permission.');
      console.error('Microphone error:', err);
    }
  }, [stt, onTranscript]);

  const speak = useCallback(
    async (text: string) => {
      if (!voiceEnabled || !autoSpeak || !text.trim()) return;

      setVoiceState('speaking');
      try {
        const result = await tts({ text, voice, model }).unwrap();
        if (result.audio && audioElementRef.current) {
          const audioBlob = base64ToBlob(result.audio, 'audio/mpeg');
          const url = URL.createObjectURL(audioBlob);
          audioElementRef.current.src = url;
          audioElementRef.current.play().catch(() => {
            setVoiceState('idle');
          });
        }
      } catch (err) {
        console.error('TTS error:', err);
        setError('Speech playback failed. Please try again.');
        setVoiceState('idle');
      }
    },
    [voiceEnabled, autoSpeak, tts, voice, model],
  );

  const stopSpeaking = useCallback(() => {
    audioElementRef.current?.pause();
    if (audioElementRef.current) {
      audioElementRef.current.currentTime = 0;
    }
    setVoiceState((prev) => (prev === 'speaking' ? 'idle' : prev));
  }, []);

  return {
    voiceEnabled,
    voiceState,
    error,
    toggleVoice,
    startRecording,
    stopRecording,
    speak,
    stopSpeaking,
  };
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(b64: string, contentType: string): Blob {
  const bytes = atob(b64);
  const array = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    array[i] = bytes.charCodeAt(i);
  }
  return new Blob([array], { type: contentType });
}
